import { useState, useCallback, useRef, useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import { _js } from '@ifixit/localize';
import { fireGlobal } from 'Shared/utils/global-events';
import { Geo } from 'Shared/utils/geo';
import Validation from 'Shared/validation';
import { Recaptcha } from 'Shared/recaptcha';
import { recaptchaSiteKeyFor } from 'Shared/recaptcha-site-key';
import { registerWithCode } from '../services/authService';
import { withLoadingIndicator } from '../utils/loadingUtils';
import { shopifySsoAndGoToLastPage } from '../utils/shopifyUtils';
import type {
   AuthResponse,
   JoinFormValues,
   RegisterParams,
   WindowApp,
   WindowGuideConstants,
} from '../types';

interface UseRegisterCodeProps {
   email: string;
   privacyPolicyUrl?: string; // Make optional since it's not used
   onJoinComplete?: () => void;
}

/**
 * Retrieves user library constants for error mapping
 */
const getUserLibConstants = (): Record<string, string> => {
   const userLib = (window as any).guide_constants?.UserLib as WindowGuideConstants['UserLib'];
   if (!userLib) return {};

   return {
      ERR_USERNAME_INVALID: userLib('ERR_USERNAME_INVALID'),
      ERR_EMAIL_INVALID: userLib('ERR_EMAIL_INVALID'),
      ERR_EMAIL_EXISTS: userLib('ERR_EMAIL_EXISTS'),
      ERR_EMAIL_VERIFICATION: userLib('ERR_EMAIL_VERIFICATION'),
      ERR_DISALLOWED: userLib('ERR_DISALLOWED'),
      ERR_USER_LIMIT: userLib('ERR_USER_LIMIT'),
      ERR_CAPTCHA_FAIL: userLib('ERR_CAPTCHA_FAIL'),
   };
};

/**
 * Validates form values and returns validation errors
 */
const validateFormValues = (values: JoinFormValues): Partial<JoinFormValues> => {
   const validationErrors: Partial<JoinFormValues> = {};
   const app = (window as any).App as WindowApp;

   if (!Validation.email(values.email)) {
      validationErrors.email = 'Please enter a valid email address.';
   }

   if (!Validation.username(values.name)) {
      validationErrors.name = app?.registerMessages?.username_invalid || _js('Username is invalid');
   }

   return validationErrors;
};

/**
 * Validates registration code format
 */
const validateRegistrationCode = (code: string): string => {
   if (!code || code.length !== 6) {
      return _js('Please enter a valid 6-digit code.');
   }
   return '';
};

/**
 * Handles successful registration by firing events and redirecting
 */
const handleRegistrationSuccess = (response: AuthResponse, onJoinComplete?: () => void) => {
   const app = (window as any).App as WindowApp;

   if (app?.which === 'ifixit') {
      (window as any).fbq?.('track', 'CompleteRegistration', {
         content_name: 'Account',
      });
   }

   fireGlobal('login.success', response);
   if (onJoinComplete) {
      onJoinComplete();
   } else {
      shopifySsoAndGoToLastPage();
   }
};

/**
 * Handles failed registration by firing events and returning error details
 */
const handleRegistrationFailure = (response: AuthResponse) => {
   fireGlobal('login.failure', response);
   return mapErrorToField(response);
};

/**
 * Maps error responses to specific form fields or code field
 */
const mapErrorToField = (response: AuthResponse) => {
   const userLibConstants = getUserLibConstants();
   const errorFields: Record<string, keyof JoinFormValues | 'code'> = {
      [userLibConstants.ERR_USERNAME_INVALID]: 'name',
      [userLibConstants.ERR_EMAIL_INVALID]: 'email',
      [userLibConstants.ERR_EMAIL_EXISTS]: 'email',
      [userLibConstants.ERR_EMAIL_VERIFICATION]: 'email',
      [userLibConstants.ERR_DISALLOWED]: 'email',
      [userLibConstants.ERR_USER_LIMIT]: 'email',
      [userLibConstants.ERR_CAPTCHA_FAIL]: 'email',
      invalid_code: 'code',
      expired_code: 'code',
   };

   const errorField = errorFields[response.reason || ''];
   const errorMessage =
      response.message || response.reason || _js('An error occurred. Please try again.');

   return {
      field: errorField || ('code' as const),
      message: errorMessage,
   };
};

/**
 * Executes reCAPTCHA and returns the result
 */
const executeRecaptcha = async (
   recaptchaRef: HTMLDivElement | null,
   siteKey: string | null
): Promise<string> => Recaptcha.executeInvisible(recaptchaRef, siteKey, 'register');

/**
 * Builds registration parameters from form values
 */
const buildRegistrationParams = (
   email: string,
   formValues: JoinFormValues,
   code: string,
   captchaResult: string
): RegisterParams => {
   const app = (window as any).App as WindowApp;

   return {
      login: email,
      username: formValues.name,
      code,
      inviteCode: app?.inviteCode ?? null,
      wantsDigestEmails: formValues.wantsDigestEmails,
      wantsNewsEmails: formValues.wantsNewsEmails,
      captchaResult,
   };
};

export const useRegisterCode = ({
   email,
   privacyPolicyUrl: _privacyPolicyUrl,
   onJoinComplete,
}: UseRegisterCodeProps) => {
   const [formValues, setFormValues] = useState<JoinFormValues>({
      email,
      name: '',
      wantsDigestEmails: false,
      wantsNewsEmails: false,
   });

   const [code, setCode] = useState('');
   const [codeError, setCodeError] = useState<string>('');
   const [errors, setErrors] = useState<Partial<JoinFormValues>>({});
   const [touched, setTouched] = useState<Partial<Record<keyof JoinFormValues, boolean>>>({});

   const recaptchaPlaceholderRef = useRef<HTMLDivElement>(null);

   // Set newsletter defaults based on user's geographic location
   useEffect(() => {
      Geo.runEverywhereButEU(() => {
         setFormValues(prev => ({
            ...prev,
            wantsDigestEmails: true,
            wantsNewsEmails: true,
         }));
      });
   }, []);

   const registrationMutation = useMutation({
      mutationFn: async (params: {
         email: string;
         formValues: JoinFormValues;
         code: string;
         recaptchaRef: HTMLDivElement | null;
      }) => {
         const captchaResult = await executeRecaptcha(
            params.recaptchaRef,
            recaptchaSiteKeyFor('register')
         );
         const registrationParams = buildRegistrationParams(
            params.email,
            params.formValues,
            params.code,
            captchaResult
         );
         return registerWithCode(registrationParams);
      },
      onSuccess: (response: AuthResponse) => handleRegistrationSuccess(response, onJoinComplete),
      onError: (response: AuthResponse) => {
         const errorInfo = handleRegistrationFailure(response);

         if (errorInfo.field === 'code') {
            setCodeError(errorInfo.message);
         } else {
            // errorInfo.field is a form field
            const formField = errorInfo.field as keyof JoinFormValues;
            setErrors(prev => ({ ...prev, [formField]: errorInfo.message }));
         }
      },
   });

   const handleInputChange = useCallback((field: keyof JoinFormValues, value: string | boolean) => {
      setFormValues(prev => ({ ...prev, [field]: value }));
      // Clear error when user starts typing
      setErrors(prev => (prev[field] ? { ...prev, [field]: undefined } : prev));
   }, []);

   const handleInputBlur = useCallback(
      (field: keyof JoinFormValues) => {
         setTouched(prev => ({ ...prev, [field]: true }));
         const validationErrors = validateFormValues(formValues);
         if (validationErrors[field]) {
            setErrors(prev => ({ ...prev, [field]: validationErrors[field] }));
         }
      },
      [formValues]
   );

   const handleSubmit = useCallback(async () => {
      const formValidationErrors = validateFormValues(formValues);
      setErrors(formValidationErrors);

      const codeValidationError = validateRegistrationCode(code);
      setCodeError(codeValidationError);

      if (Object.keys(formValidationErrors).length > 0 || codeValidationError) {
         return;
      }

      try {
         await withLoadingIndicator(
            registrationMutation.mutateAsync({
               email,
               formValues,
               code,
               recaptchaRef: recaptchaPlaceholderRef.current,
            }),
            {
               pendingMessage: _js('Registering your account...'),
               successMessage: _js('Registration successful'),
               failureMessage: _js('Registration unsuccessful'),
            }
         );
      } catch (error) {
         console.error('Error during registration:', error);
         // Error is already handled in onError callback
      }
   }, [formValues, code, email, registrationMutation]);

   return {
      formValues,
      code,
      setCode,
      codeError,
      errors,
      touched,
      isSubmitting: registrationMutation.isPending,
      recaptchaPlaceholderRef,
      handleInputChange,
      handleInputBlur,
      handleSubmit,
   };
};
